import React, { useState } from 'react';
import OptionDialog from './OptionDialog';
import MapBoundsDialog from './MapBoundsDialog';
import ConditionValues from './ConditionValues';
import M3Dialog, { DialogTitle, DialogBody, DialogActions } from '../M3Dialog';

const OPS = [
  { value: 'between', label: 'between' },
  { value: 'in', label: 'in' },
  { value: 'eq', label: 'equals' },
  { value: 'is_null', label: 'is empty' },
  { value: 'is_not_null', label: 'is not empty' },
  { value: 'larger_than', label: 'larger than' }
];
const TAG_OPS = [{ value: 'in', label: 'in' }];
const SITE_OPS = [
  { value: 'in', label: 'in' },
  { value: 'within_bbox', label: 'within boundaries' },
  { value: 'is_not_null', label: 'has site' }
];

// One filter condition: field (from the entity + its joins), operator and
// values. Tags and sites pick their values from dialogs: a checkbox list
// (tags, site "in") or a map rectangle (site "within boundaries").
export default function ConditionRow({
  fields,
  condition,
  joins = [],
  logic,
  onChange,
  onRemove,
  onLogicChange
}) {
  const field = (fields || []).find((f) => f.key === condition.field);
  const isTags = field && field.kind === 'tags';
  const isSites = field && field.kind === 'sites';
  const op = isTags ? 'in' : isSites ? condition.op || 'in' : condition.op || 'eq';
  const ops = isTags ? TAG_OPS : isSites ? SITE_OPS : OPS;
  const [pickerOpen, setPickerOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const setField = (key) => {
    const next = (fields || []).find((f) => f.key === key);
    const patch = { field: key, target: targetFor(next, joins), values: [] };
    if (next && (next.kind === 'tags' || next.kind === 'sites')) patch.op = 'in';
    onChange({ ...condition, ...patch });
  };

  const setOp = (next) => onChange({ ...condition, op: next, values: [] });

  const commitValues = (values) => {
    setPickerOpen(false);
    setMapOpen(false);
    onChange({ ...condition, values });
  };

  return (
    <div className="condition-row">
      {onLogicChange && (
        <select
          className="condition-logic"
          aria-label="Logic"
          value={logic || 'and'}
          onChange={(e) => onLogicChange(e.target.value)}
        >
          <option value="and">and</option>
          <option value="or">or</option>
        </select>
      )}
      <button type="button" className="condition-edit" onClick={() => setDialogOpen(true)}>
        Edit condition
      </button>
      <button className="condition-remove" onClick={onRemove} title="Remove filter">
        x
      </button>
      {dialogOpen && (
        <M3Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
          <M3Dialog.Title>Condition</M3Dialog.Title>
          <M3Dialog.Body>
            {onLogicChange && (
              <select
                className="condition-logic"
                aria-label="Logic"
                value={logic || 'and'}
                onChange={(e) => onLogicChange(e.target.value)}
              >
                <option value="and">and</option>
                <option value="or">or</option>
              </select>
            )}
            <select
              className="condition-field"
              value={condition.field || ''}
              onChange={(e) => setField(e.target.value)}
            >
              <option value="">field…</option>
              {(fields || []).map((f) => (
                <option key={f.key} value={f.key}>
                  {f.fullLabel || f.label || f.key}
                </option>
              ))}
            </select>
            <select
              className="condition-op"
              value={op}
              onChange={(e) => setOp(e.target.value)}
            >
              {ops.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <ConditionValues
              field={field}
              op={op}
              condition={condition}
              onChange={onChange}
              onPickOptions={() => setPickerOpen(true)}
              onPickRegion={() => setMapOpen(true)}
            />
          </M3Dialog.Body>
          <M3Dialog.Actions>
            <button onClick={() => setDialogOpen(false)}>Cancel</button>
            <button onClick={() => setDialogOpen(false)}>Apply</button>
          </M3Dialog.Actions>
        </M3Dialog>
      )}
      {pickerOpen && (
        <OptionDialog
          title={isTags ? 'Select tags' : 'Select sites'}
          options={field.options}
          selected={condition.values}
          onApply={commitValues}
          onClose={() => setPickerOpen(false)}
        />
      )}
      {mapOpen && (
        <MapBoundsDialog
          title="Select site region"
          sites={field.options}
          bounds={condition.values}
          onApply={commitValues}
          onClose={() => setMapOpen(false)}
        />
      )}
    </div>
  )
}

function targetFor(field, joins) {
  if (!field) return 'entity';
  // The site pseudo-field has no join prefix on its key, so it is mapped to
  // its join explicitly; tags stay an entity field.
  if (field.kind === 'sites' && joins.includes('site')) return 'site';
  const m = field.key.match(/^(.+?)_/);
  if (m && joins.includes(m[1])) return m[1];
  return 'entity';
}
